import { connectMenu } from "instantsearch.js/es/connectors";
import { curatedFacetRefinementsSpacer, facetNoResultsLabel, isCuratedLayoutPage } from "../curated-layout";
import { curatedSearchConfig } from "@/services/algoliaRouting.blade";
import { escapeHtml, runAfterFrame } from "./helpers";

export { MAKE_DEPENDENT_REFINEMENT_FACETS } from "@/services/algoliaRouting.blade";

interface MakeFacetItem {
    value: string;
    label: string;
    count: number;
    isRefined: boolean;
}

const MAKE_FACET_CONTAINER_SELECTORS = [
    "#action-bar-make",
    "#menu-make",
] as const;

function getMakeFacetContainers(): HTMLElement[] {
    return MAKE_FACET_CONTAINER_SELECTORS.flatMap((selector) => {
        const el = document.querySelector<HTMLElement>(selector);
        return el ? [el] : [];
    });
}

/**
 * Make facet (single-select menu) matching Vue's ais-menu + Menu.vue markup.
 * Renders into the sidebar (#menu-make) and the tablet+ action-bar dropdown
 * (#action-bar-make).
 *
 * Brand list is frozen from SSR `initialMakesFacet` so brands never disappear
 * when other facets narrow the result set.
 *
 * Uses connectMenu (not refinementList) so menu[make] URL params and Typesense
 * facet filtering stay in sync — required for dependent facets (model, etc.).
 */
export function createMakeMenuWidget() {
    let localSearchQuery = "";
    let listenersBound = false;
    let latestRefine: ((value: string) => void) | null = null;

    const frozenBrands: MakeFacetItem[] = (
        window.__MARKETPLACE_CONFIG__?.initialMakesFacet ?? []
    ).map((b) => ({ ...b }));

    return connectMenu((renderOptions, isFirstRender) => {
        const containers = getMakeFacetContainers();
        if (!containers.length) return;

        const { items, refine } = renderOptions;
        latestRefine = refine;
        const instantSearchInstance = (
            renderOptions as typeof renderOptions & {
                instantSearchInstance: {
                    scheduleRender: () => void;
                    getUiState: () => Record<
                        string,
                        {
                            menu?: { make?: string };
                            refinementList?: { make?: string[] };
                        }
                    >;
                };
            }
        ).instantSearchInstance;

        const indexName =
            window.__MARKETPLACE_CONFIG__?.search?.indexName ?? "";
        const indexUiState = indexName
            ? instantSearchInstance.getUiState()?.[indexName]
            : undefined;

        let selectedMake =
            indexUiState?.menu?.make ??
            indexUiState?.refinementList?.make?.[0];

        const curatedFilters = curatedSearchConfig.value.filters;
        if (curatedSearchConfig.value.isActive && curatedFilters) {
            selectedMake =
                curatedFilters.menu?.make ??
                curatedFilters.refinementList?.make?.[0] ??
                selectedMake;
        }

        const liveByValue = new Map(items.map((i) => [i.value, i]));

        // Curated page: Vue show() passes no initialMakesFacet, so Marketplace.vue keeps an
        // empty make list (« Pas de résultats »); reproduced as is.
        const mergedItems: MakeFacetItem[] = frozenBrands.length || isCuratedLayoutPage()
            ? frozenBrands.map((frozen) => {
                  const live = liveByValue.get(frozen.value);
                  return {
                      value: frozen.value,
                      label: frozen.label,
                      count: live?.count ?? frozen.count,
                      isRefined: selectedMake
                          ? frozen.value === selectedMake
                          : (live?.isRefined ?? false),
                  };
              })
            : items.map((i) => ({
                  value: i.value,
                  label: i.label,
                  count: i.count,
                  isRefined: selectedMake
                      ? i.value === selectedMake
                      : i.isRefined,
              }));

        const q = localSearchQuery.trim().toLowerCase();
        const displayItems = q
            ? mergedItems.filter((i) => i.label.toLowerCase().includes(q))
            : mergedItems;
        const hasSelected = mergedItems.some((i) => i.isRefined);

        const escapeAttr = (s: string) =>
            s.replace(/\\/g, "\\\\").replace(/"/g, "&quot;");

        const listHtml = displayItems.length
            ? displayItems
                  .map((item) => {
                      const valAttr = escapeAttr(item.value);
                      const labHtml = escapeHtml(item.label);
                      return `<label class="RefinementList${
                          item.isRefined ? " RefinementList--selected" : ""
                      }">
              <span>
                <span class="RefinementList__labelText">${labHtml}</span>
                <span class="RefinementList__count">(${item.count.toLocaleString(
                    "fr-FR",
                )})</span>
              </span>
              <input type="checkbox" class="RefinementList__checkbox RefinementList__checkbox--rounded" value="${valAttr}"${
                  item.isRefined ? " checked" : ""
              } />
            </label>`;
                  })
                  .join("")
            : `<span class="no-results-label">${facetNoResultsLabel()}</span>`;

        const pinnedItem = selectedMake
            ? mergedItems.find((i) => i.value === selectedMake)
            : undefined;
        // Curated page: Menu.vue's in-facet CurrentRefinement (createCuratedFacetWidgets).
        const pinnedHtml = isCuratedLayoutPage()
            ? curatedFacetRefinementsSpacer("make")
            : pinnedItem
            ? `<div class="current-refinements facet" data-make-pinned>
            <div>
              <label class="RefinementList RefinementList--selected">
                <span><span class="RefinementList__labelText">${escapeHtml(
                    pinnedItem.label,
                )}</span></span>
                <input type="checkbox" class="RefinementList__clear" value="${escapeAttr(
                    pinnedItem.value,
                )}" checked />
              </label>
            </div>
          </div>`
            : `<div class="current-refinements facet" data-make-pinned hidden><div></div></div>`;

        const markup = `<div class="make-menu-facet">
            <div class="RefinementList__search input__field input__field--facette">
              <input type="search" class="input input--search" placeholder="Rechercher une marque…" data-make-facet-search autocomplete="off" />
            </div>
            ${pinnedHtml}
            <div class="RefinementList__wrapper${
                hasSelected ? " RefinementList__wrapper--has-selected" : ""
            }">${listHtml}</div>
          </div>`;

        for (const container of containers) {
            if (isFirstRender) {
                container.innerHTML = markup;
            } else {
                const pinned = container.querySelector("[data-make-pinned]");
                if (pinned) {
                    pinned.outerHTML = pinnedHtml;
                }
                const wrap = container.querySelector(
                    ".RefinementList__wrapper",
                );
                if (wrap) {
                    wrap.classList.toggle(
                        "RefinementList__wrapper--has-selected",
                        hasSelected,
                    );
                    wrap.innerHTML = listHtml;
                }
            }
        }

        if (!listenersBound && isFirstRender) {
            listenersBound = true;
            const primary = containers[0];

            primary
                .querySelector<HTMLInputElement>("[data-make-facet-search]")
                ?.addEventListener("input", (event) => {
                    const input = event.target as HTMLInputElement;
                    localSearchQuery = input.value;
                    containers.slice(1).forEach((container) => {
                        const mirror =
                            container.querySelector<HTMLInputElement>(
                                "[data-make-facet-search]",
                            );
                        if (mirror && mirror !== input) {
                            mirror.value = input.value;
                        }
                    });
                    instantSearchInstance.scheduleRender();
                });

            const onFacetInteraction = (event: Event) => {
                const target = event.target as HTMLInputElement;
                if (target.classList.contains("RefinementList__checkbox")) {
                    // Read `checked` now; the deferred refine runs after the
                    // next paint so the tap's INP window stays short.
                    const next = target.checked ? target.value : "";
                    runAfterFrame(() => latestRefine?.(next));
                    return;
                }
                if (target.classList.contains("RefinementList__clear")) {
                    event.preventDefault();
                    runAfterFrame(() => latestRefine?.(""));
                }
            };

            containers.forEach((container) => {
                container
                    .querySelector(".make-menu-facet")
                    ?.addEventListener("change", onFacetInteraction);
            });
        }
    })({
        attribute: "make",
        sortBy: ["name:asc"],
    });
}
